import React, { useState } from "react";
import {
  useForm,
  Controller,
  SubmitHandler,
  FieldError,
} from "react-hook-form";
import {
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Spinner,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import { baseUrl } from "../Utils/Baseurl/Baseurl";
import { jwtDecode } from "jwt-decode";
import { LoginFormProp } from "../Types/Others.type";
import CommonLogo from "../Components/Pages/Others/Authentication/Common/CommonLogo";

interface SignInFormData {
  email: string;
  password: string;
}

interface ResetPasswordData {
  newPassword: string;
  confirmPassword: string;
}

export default function SignInForm({ logoClass }: LoginFormProp) {
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SignInFormData>();

  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [forgotPasswordModal, setForgotPasswordModal] = useState(false);
  const [emailForReset, setEmailForReset] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    control: resetControl,
    handleSubmit: handleResetSubmit,
    formState: { errors: resetErrors },
    watch,
  } = useForm<ResetPasswordData>();

  const togglePasswordVisibility = () => setPasswordVisible(!isPasswordVisible);
  const toggleForgotPasswordModal = () =>
    setForgotPasswordModal(!forgotPasswordModal);

  const onSubmit: SubmitHandler<SignInFormData> = async (data) => {
    try {
      const response = await axios.post(baseUrl + "login/loginuser", data);

      if (response.data.token) {
        if (response.data.disablestatus === 1) {
          toast.error(
            "Your account has been disabled. Please contact support."
          );
          return; // Stop login process if account is disabled
        }

        toast.success("Login Success...!");
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userId", response.data.id);

        const decoded = jwtDecode<{ id: string; usertype: string }>(
          response.data.token
        );
        localStorage.setItem("usertype", decoded.usertype || "");

        navigate(`${process.env.PUBLIC_URL}/dashboard/default`);
      } else {
        toast.error(response.data.message || "Invalid email or password");
      }
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        let errorMessage = error.response.data.message;

        // Remove "@" before displaying error messages
        errorMessage = errorMessage.replace("@", "");

        if (
          // errorMessage.includes("Email must be in Gmail format") 
          errorMessage.includes(
            "Email should not start with special characters"
          ) ||
          errorMessage.includes(
            "Email should not contain leading or trailing spaces"
          )
        ) {
          setError("email", { type: "manual", message: errorMessage });
        } else if (errorMessage.includes("Invalid email or password")) {
          setError("password", { type: "manual", message: errorMessage });
        }

        toast.error(errorMessage);
      }
    }
  };

  // ** Handle Password Reset Form Submission **
  const onResetSubmit: SubmitHandler<ResetPasswordData> = async (data) => {
    if (!emailForReset) {
      toast.error("Please enter your email first.");
      return;
    }
    setLoading(true); // Show loader
    try {
      const response = await axios.post(baseUrl + "user/changePassword", {
        email: emailForReset,
      });

      console.log(response.data); // Debugging

      if (response.data.success) {
        toast.dismiss(); // Clear any old toasts
        toast.success(" Reset link sent successfully", {
          // theme: "colored",
        });

        // Close modal after 1.5 seconds for better UX
        setTimeout(() => {
          toggleForgotPasswordModal();
        }, 1500);
      } else {
        toast.error(response.data.message || "Failed to update password.");
      }
    } catch (error) {
      toast.error("An error occurred while updating the password.");
    } finally {
      setLoading(false); // Hide loader after request completes
    }
  };

  return (
    <>
      <Col
        xs={12}
        sm={10}
        md={8}
        lg={6}
        style={{ maxWidth: "350px", width: "100%" }}
      >
        {/* Scrollable form container */}
        <div
          style={{
            background: "#fff",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
            maxHeight: "calc(88vh - 40px)",
            overflowY: "auto",
          }}
        >
          <Form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
            <div style={{ textAlign: "center" }}>
              <CommonLogo logoClass="logo-class" />
            </div>
            <h2
              style={{
                textAlign: "center",
                marginTop: "0px",
                marginBottom: "10px",
                fontSize: "22px",
              }}
            >
              Sign In to Your Account
            </h2>
            <p
              style={{
                textAlign: "center",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              Enter your email & password to login
            </p>

            <FormGroup>
              <Label>Email Address</Label>
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Input
                    type="email"
                    {...field}
                    placeholder="Email"
                    invalid={!!errors.email}
                    onChange={(e) => {
                      field.onChange(e);
                      setEmailForReset(e.target.value);
                    }}
                  />
                )}
              />
              {errors.email && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {errors.email.message}
                </div>
              )}
            </FormGroup>
            <FormGroup>
              <Label>Password</Label>
              <Controller
                name="password"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <div
                    style={{
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Input
                      type={isPasswordVisible ? "text" : "password"}
                      {...field}
                      placeholder="Password"
                      invalid={!!errors.password}
                      style={{ paddingRight: "60px" }}
                    />
                    <span
                      onClick={togglePasswordVisibility}
                      style={{
                        position: "absolute",
                        right: "10px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        cursor: "pointer",
                        color: "#007bff",
                        fontSize: "14px",
                        fontWeight: "bold",
                      }}
                    >
                      {isPasswordVisible ? "Hide" : "Show"}
                    </span>
                  </div>
                )}
              />
              {errors.password && (
                <div style={{ color: "red", fontSize: "12px" }}>
                  {errors.password.message}
                </div>
              )}
            </FormGroup>

            <div style={{ textAlign: "end", marginBottom: "10px" }}>
              <Link
                to="#"
                className="text-primary"
                onClick={toggleForgotPasswordModal}
              >
                Forgot Password?
              </Link>
            </div>

            <FormGroup>
              <Button
                color="primary"
                style={{ width: "100%", padding: "10px", fontSize: "16px" }}
                type="submit"
              >
                Sign In
              </Button>
            </FormGroup>

            <div style={{ textAlign: "center", marginTop: "10px" }}>
              <p style={{ fontSize: "14px" }}>Don't have an account?</p>
              <div style={{ marginTop: "5px" }}>
                <Link
                  to="/register"
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    display: "block",
                    marginBottom: "5px",
                  }}
                >
                  Register Now
                </Link>
                {/* <Link
                  to="/registeremployee"
                  style={{
                    color: "#007bff",
                    textDecoration: "none",
                    display: "block",
                  }}
                >
                  Register As Employee
                </Link> */}
              </div>
            </div>
          </Form>
        </div>
      </Col>
      {/* Forgot Password Modal */}
      <Modal isOpen={forgotPasswordModal} toggle={toggleForgotPasswordModal}>
        <ModalHeader toggle={toggleForgotPasswordModal}>
          Reset Password
        </ModalHeader>
        <ModalBody>
          <Form onSubmit={handleResetSubmit(onResetSubmit)}>
            <FormGroup>
              <Label>Email Address</Label>
              <Input
                type="email"
                value={emailForReset}
                onChange={(e) => setEmailForReset(e.target.value)}
                placeholder="Enter your registered email"
              />
            </FormGroup>

            <ModalFooter>
              <Button color="primary" type="submit">
                Reset Password
              </Button>
              <Button color="secondary" onClick={toggleForgotPasswordModal}>
                Cancel
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </Modal>

      {/* Loader Modal */}
      <Modal isOpen={loading} centered backdrop="static">
        <ModalBody className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Please wait...</p>
        </ModalBody>
      </Modal>
    </>
  );
}
